import React, { useEffect, useState } from "react";
import { ProDeliveryDelay } from "@/components/ProDeliveryDelay";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Lock, Minus, Plus, ShoppingCart, Check } from "lucide-react";
import { toast } from "sonner";
import { proApi, eurNum, imgSrc, errMsg } from "@/lib/proApi";
import { usePro } from "@/context/ProContext";
import { PayIn4 } from "@/components/PayIn4";
import { useSeo, useJsonLd, SITE_URL } from "@/lib/seo";

const Stepper = ({ value, onChange, testid }) => (
  <div className="inline-flex items-center rounded-full border border-[var(--oz-border)] bg-white">
    <button type="button" aria-label="Moins" data-testid={`${testid}-minus`} onClick={() => onChange(Math.max(1, value - 1))} className="p-2.5"><Minus className="w-4 h-4" /></button>
    <span data-testid={`${testid}-value`} className="w-10 text-center font-semibold">{value}</span>
    <button type="button" aria-label="Plus" data-testid={`${testid}-plus`} onClick={() => onChange(Math.min(999, value + 1))} className="p-2.5"><Plus className="w-4 h-4" /></button>
  </div>
);
export { Stepper };

export default function ProProduct() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user, approved, addItem, config } = usePro();
  const [p, setP] = useState(null);
  const [size, setSize] = useState(null);
  const [lots, setLots] = useState(1);
  const [busy, setBusy] = useState(false);
  useSeo({
    title: p ? `${p.name} pour professionnels, par lots de ${p.pack_sizes[0]} pièces | Ozentya Pro` : "Espace Pro Ozentya",
    description: p ? `${p.name} pour salons, barbiers et boutiques : ${p.description}`.slice(0, 158) : "",
    path: `/espace-pro/produit/${slug}`,
    image: p?.image && !p.image.startsWith("/api/") ? `${SITE_URL}${p.image}` : undefined,
  });
  useJsonLd("ld-product", p ? {
    "@context": "https://schema.org", "@type": "Product", name: p.name, description: p.description, category: p.category,
    brand: { "@type": "Brand", name: "Ozentya" }, sku: p.slug, url: `${SITE_URL}/espace-pro/produit/${p.slug}`,
    image: p.image && !p.image.startsWith("/api/") ? `${SITE_URL}${p.image}` : undefined,
  } : null);
  useJsonLd("ld-breadcrumb", p ? {
    "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Catalogue professionnel", item: `${SITE_URL}/espace-pro` },
      { "@type": "ListItem", position: 3, name: p.name, item: `${SITE_URL}/espace-pro/produit/${p.slug}` }],
  } : null);

  const ready = user !== null;
  useEffect(() => {
    if (!ready) return;
    proApi.get(`/pro/products/${slug}`).then(({ data }) => {
      setP(data);
      setSize((s) => (data.pack_sizes.includes(s) ? s : data.pack_sizes[0]));
    }).catch(() => setP(false));
  }, [slug, ready, approved]);

  if (p === false) return <div className="py-24 text-center"><p className="font-serif text-2xl">Produit introuvable</p><Link to="/espace-pro" className="text-[var(--oz-gold)] font-semibold">Retour au catalogue</Link></div>;
  if (!p) return <div className="py-24" />;

  const vat = config?.vat_rate ?? 20;
  const pieces = size * lots;
  const ht = approved ? Math.round(p.unit_price_ht * 100) * pieces / 100 : 0;
  const ttc = Math.round(ht * 100 * (1 + vat / 100)) / 100;

  const add = async () => {
    setBusy(true);
    try {
      await addItem(p.id, size, lots);
      toast.success(`${lots > 1 ? `${lots} × ` : ""}${size} pièces ajoutées au panier`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`pro-product-${p.slug}`} className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16 py-10">
      <Link to="/espace-pro" className="inline-flex items-center gap-1.5 text-sm text-[var(--oz-muted)] py-2"><ArrowLeft className="w-4 h-4" /> Catalogue</Link>
      <div className="mt-4 grid md:grid-cols-2 gap-10">
        <div className="rounded-3xl overflow-hidden bg-[var(--oz-bg-2)] aspect-square">
          <img src={imgSrc(p.image)} alt={p.name} className="w-full h-full object-cover" />
        </div>
        <div>
          <span className="text-xs uppercase tracking-[0.16em] text-[var(--oz-gold)] font-semibold">{p.category}</span>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-[var(--oz-text)] leading-tight">{p.name}</h1>
          {p.description && <p className="mt-4 text-[var(--oz-muted)] leading-relaxed">{p.description}</p>}

          {!approved ? (
            <div className="mt-8 rounded-2xl border border-[var(--oz-border)] bg-white p-6">
              <p className="flex items-center gap-2 font-semibold"><Lock className="w-4 h-4 text-[var(--oz-gold)]" /> Prix réservés aux professionnels</p>
              <p className="mt-2 text-sm text-[var(--oz-muted)]">Quantités proposées : {p.pack_sizes.join(" ou ")} pièces.</p>
              {!user && <div className="mt-4 flex gap-3">
                <Link to={`/espace-pro/connexion?next=/espace-pro/produit/${p.slug}`} data-testid="pro-product-login" className="px-5 py-3 rounded-full bg-[var(--oz-dark)] text-white text-sm font-semibold">Se connecter</Link>
                <Link to="/espace-pro/inscription" className="px-5 py-3 rounded-full border border-[var(--oz-border)] text-sm font-semibold">Créer un compte</Link>
              </div>}
              {user && <p className="mt-3 text-sm text-[var(--oz-badge)]">Compte en attente de validation.</p>}
            </div>
          ) : (
            <div className="mt-8 space-y-6">
              <p data-testid="pro-unit-price" className="text-2xl font-semibold">{eurNum(p.unit_price_ht)} <span className="text-sm font-normal text-[var(--oz-muted)]">HT / pièce</span></p>
              <div>
                <p className="text-sm font-semibold mb-2">Quantité</p>
                <div className="flex flex-wrap gap-2">
                  {p.pack_sizes.map((s) => (
                    <button key={s} onClick={() => setSize(s)} data-testid={`pro-pack-${s}`}
                      className={`px-4 py-2.5 rounded-xl border text-sm font-semibold transition-colors ${size === s ? "bg-[var(--oz-dark)] text-white border-[var(--oz-dark)]" : "border-[var(--oz-border)] hover:border-[var(--oz-dark)]"}`}>
                      {size === s && <Check className="w-3.5 h-3.5 inline mr-1" />}{s} pièces
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold mb-2">Nombre de fois</p>
                <Stepper value={lots} onChange={setLots} testid="pro-lots" />
              </div>
              <div className="rounded-2xl bg-white border border-[var(--oz-border)] p-5 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[var(--oz-muted)]">{size} pièces × {eurNum(p.unit_price_ht)}</span><span data-testid="pro-lot-price">{eurNum(Math.round(p.unit_price_ht * 100) * size / 100)} HT</span></div>
                <div className="flex justify-between"><span className="text-[var(--oz-muted)]">Quantité choisie</span><span data-testid="pro-total-pieces">{lots > 1 ? `${lots} × ${size} = ` : ""}{pieces} pièces</span></div>
                <div className="flex justify-between font-semibold text-base"><span>Total HT</span><span data-testid="pro-total-ht">{eurNum(ht)}</span></div>
                <div className="flex justify-between text-[var(--oz-muted)]"><span>Total TTC (TVA {String(vat).replace(".", ",")} %)</span><span data-testid="pro-total-ttc">{eurNum(ttc)}</span></div>
              </div>
              <PayIn4 amount={ttc} testid="pro-product-pay4" />
              <div className="flex flex-wrap gap-3">
                <button onClick={add} disabled={busy} data-testid="pro-add-to-cart" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[var(--oz-gold)] text-[var(--oz-dark)] font-semibold disabled:opacity-60">
                  <ShoppingCart className="w-4 h-4" /> Ajouter au panier
                </button>
                <button onClick={() => nav("/espace-pro/panier")} data-testid="pro-go-cart" className="px-6 py-3.5 rounded-full border border-[var(--oz-border)] font-semibold">Voir le panier</button>
              </div>
              <ProDeliveryDelay testid="pro-product-delay" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
